<script setup lang="ts">
// Drawer desktop Cmd+J/Ctrl+J. Refactor P8.H : thread + composer
// extraits dans features/assistant/components/.

import { onMounted, onUnmounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useChatStore } from "@/features/assistant/stores/chat-store";
import AssistantComposer from "@/features/assistant/components/AssistantComposer.vue";
import AssistantThread from "@/features/assistant/components/AssistantThread.vue";
import Button from "@/shared/ui/Button.vue";

const store = useChatStore();
const { open, conversations, currentConversationId } = storeToRefs(store);
const composerRef = ref<InstanceType<typeof AssistantComposer> | null>(null);
const accountId = ref(import.meta.env["VITE_DEFAULT_ACCOUNT_ID"] ?? "");

function isCmdJ(e: KeyboardEvent): boolean {
  return (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j";
}

function onKeydown(e: KeyboardEvent): void {
  if (isCmdJ(e)) {
    e.preventDefault();
    store.toggleDrawer();
    if (open.value) setTimeout(() => composerRef.value?.focus(), 50);
    return;
  }
  if (e.key === "Escape" && open.value) store.closeDrawer();
}

onMounted(() => document.addEventListener("keydown", onKeydown));
onUnmounted(() => document.removeEventListener("keydown", onKeydown));
</script>

<template>
  <transition name="slide">
    <aside v-if="open" class="drawer" role="dialog" aria-label="Assistant IA">
      <header class="head">
        <h2>Assistant</h2>
        <Button variant="ghost" size="sm" @click="store.closeDrawer()">✕</Button>
      </header>

      <div class="body">
        <nav class="conv-list" aria-label="Conversations">
          <Button variant="primary" size="sm" @click="store.newConversation()">+ Nouvelle</Button>
          <ul>
            <li
              v-for="c in conversations"
              :key="c.id"
              :class="['conv', { active: currentConversationId === c.id }]"
              @click="store.openConversation(c.id)"
            >
              {{ c.title ?? "Conversation" }}
            </li>
          </ul>
        </nav>

        <AssistantThread :account-id="accountId" />
      </div>

      <AssistantComposer ref="composerRef" />
    </aside>
  </transition>
</template>

<style scoped>
.drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(440px, 100vw);
  background: var(--color-navy);
  border-left: 1px solid rgba(232, 160, 32, 0.25);
  display: grid;
  grid-template-rows: auto 1fr auto;
  z-index: 100;
}
.head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(232, 160, 32, 0.15);
}
.head h2 {
  font-family: var(--font-serif);
  font-weight: 400;
  color: var(--color-or);
  margin: 0;
}
.body {
  display: grid;
  grid-template-columns: 140px 1fr;
  overflow: hidden;
}
.conv-list {
  border-right: 1px solid rgba(232, 160, 32, 0.1);
  padding: 0.75rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.conv-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.conv {
  font-size: 0.8rem;
  color: var(--color-cream);
  padding: 0.4rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conv:hover {
  background: rgba(232, 160, 32, 0.08);
}
.conv.active {
  background: rgba(232, 160, 32, 0.15);
  color: var(--color-or);
}
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.2s ease;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
